/**
 * Brand-derived categorical palettes, in the spirit of learnui's data colour
 * picker (https://www.learnui.design/tools/data-color-picker.html#palette):
 * interpolate from the brand's primary to an end colour in a perceptual space
 * (OKLCH), sweeping lightness so neighbouring series stay distinguishable.
 */

type Oklch = { l: number; c: number; h: number };

const toLinear = (v: number) =>
  v <= 0.04045 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
const fromLinear = (v: number) =>
  v <= 0.0031308 ? v * 12.92 : 1.055 * v ** (1 / 2.4) - 0.055;

function hexToOklch(hex: string): Oklch | undefined {
  const m = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return undefined;
  const h =
    m[1].length === 3
      ? m[1]
          .split('')
          .map((c) => c + c)
          .join('')
      : m[1];
  const [r, g, b] = [0, 2, 4].map((i) =>
    toLinear(Number.parseInt(h.slice(i, i + 2), 16) / 255),
  );
  const l_ = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m_ = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s_ = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_;
  const A = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_;
  const B = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_;
  return {
    l: L,
    c: Math.hypot(A, B),
    h: ((Math.atan2(B, A) * 180) / Math.PI + 360) % 360,
  };
}

function oklchToRgb({ l, c, h }: Oklch): [number, number, number] {
  const a = c * Math.cos((h * Math.PI) / 180);
  const b = c * Math.sin((h * Math.PI) / 180);
  const l_ = (l + 0.3963377774 * a + 0.2158037573 * b) ** 3;
  const m_ = (l - 0.1055613458 * a - 0.0638541728 * b) ** 3;
  const s_ = (l - 0.0894841775 * a - 1.291485548 * b) ** 3;
  return [
    4.0767416621 * l_ - 3.3077115913 * m_ + 0.2309699292 * s_,
    -1.2684380046 * l_ + 2.6097574011 * m_ - 0.3413193965 * s_,
    -0.0041960863 * l_ - 0.7034186147 * m_ + 1.707614701 * s_,
  ];
}

/** OKLCH → hex, reducing chroma until the colour fits in sRGB. */
function oklchToHex(color: Oklch): string {
  let c = color.c;
  let rgb = oklchToRgb({ ...color, c });
  while (c > 0 && rgb.some((v) => v < -1e-4 || v > 1 + 1e-4)) {
    c -= 0.005;
    rgb = oklchToRgb({ ...color, c });
  }
  return `#${rgb
    .map((v) =>
      Math.round(fromLinear(Math.min(Math.max(v, 0), 1)) * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

const clamp = (v: number, lo: number, hi: number) =>
  Math.min(Math.max(v, lo), hi);

/** Lightness band a data colour must sit in to read on a light surface. */
const L_MIN = 0.47;
const L_MAX = 0.76;
/** Below this, a colour is too grey to carry identity. */
const C_MIN = 0.1;
/** Hue sweep used when the end colour is derived from the primary. */
const DERIVED_SPAN = 210;

/**
 * Converts to hex; if the sRGB gamut forces the colour grey at this
 * lightness, tries nearby lightness values for one that keeps its chroma.
 */
function vividHex(color: Oklch): string {
  for (const dl of [0, 0.03, -0.03, 0.06, -0.06]) {
    const l = clamp(color.l + dl, L_MIN, L_MAX);
    const hex = oklchToHex({ ...color, l });
    const got = hexToOklch(hex);
    if (got && got.c >= C_MIN + 0.005) return hex;
  }
  return oklchToHex(color);
}

/**
 * Reorders a gradient so neighbours in the result are far apart on it
 * (0, n/2, 1, n/2+1, …). Adjacent stacked segments then differ clearly
 * while the set as a whole still reads as one branded ramp.
 */
function interleave<T>(items: readonly T[]): T[] {
  const half = Math.ceil(items.length / 2);
  const out: T[] = [];
  for (let i = 0; i < half; i++) {
    out.push(items[i]);
    if (i + half < items.length) out.push(items[i + half]);
  }
  return out;
}

/**
 * `count` categorical colours running from the brand's primary towards its
 * secondary colour, in slot order (slot 1 is the primary).
 *
 * The ramp always sweeps both hue and lightness far enough for `count`
 * series to stay distinguishable: when the secondary is missing, grey or
 * too close to the primary, the end is derived by swinging the hue round
 * (as learnui's default blue → yellow palette does) and moving lightness to
 * the other side of the band.
 */
export function brandPalette(
  primary: string,
  secondary?: string,
  count = 8,
): string[] {
  const start = hexToOklch(primary);
  if (!start || count <= 0) return [];
  start.l = clamp(start.l, L_MIN, 0.68);
  start.c = Math.max(start.c, C_MIN + 0.04);
  if (count === 1) return [vividHex(start)];

  const farL = start.l < (L_MIN + L_MAX) / 2 ? L_MAX : L_MIN;
  const minSpan = Math.min(DERIVED_SPAN, 50 * (count - 1));
  const sec = secondary ? hexToOklch(secondary) : undefined;

  let end: Oklch;
  if (!sec || sec.c < C_MIN) {
    end = { l: farL, c: start.c, h: start.h + DERIVED_SPAN };
  } else {
    const gap = ((sec.h - start.h + 540) % 360) - 180;
    const dir = gap < 0 ? -1 : 1;
    end = {
      l: Math.abs(sec.l - start.l) < 0.15 ? farL : clamp(sec.l, L_MIN, L_MAX),
      c: Math.max(sec.c, C_MIN + 0.04),
      h: start.h + dir * Math.max(Math.abs(gap), minSpan),
    };
  }

  const ramp = Array.from({ length: count }, (_, i) => {
    const t = i / (count - 1);
    return vividHex({
      l: start.l + (end.l - start.l) * t,
      c: start.c + (end.c - start.c) * t,
      h: (start.h + (end.h - start.h) * t + 360) % 360,
    });
  });
  return interleave(ramp);
}
